import type { CalculationEntry } from "../../../http/concreteResin/types/calculationEntries";
import type { ChemicalAnchorForSettings } from "../../../http/concreteResin/types/settings";
import { toOrderQuantity } from "../../../utils/orderQuantity";
import { millimetersToInchFraction, roundMillimeters } from "./resinUnits";

export type CalculationEntryGroup = {
  anchor: ChemicalAnchorForSettings;
  /** In their saving order. */
  entries: CalculationEntry[];
  totalLiters: number;
  /** To order for the total volume: whole cartridges, by the anchor's order multiple; 0 without a cartridge volume. */
  cartridges: number;
};

// The saved entries grouped by anchor, sorted by its code, as the PDF has them (report.go's
// groupTotals): the cartridges of each group's total volume, not the sum of each entry's own. An entry
// whose anchor isn't in anchors (the settings aren't loaded yet) is left out.
export const groupCalculationEntries = (
  entries: readonly CalculationEntry[],
  anchors: readonly ChemicalAnchorForSettings[],
): CalculationEntryGroup[] => {
  const anchorById = new Map(anchors.map((anchor) => [anchor.id, anchor]));
  const groups = new Map<string, Omit<CalculationEntryGroup, "cartridges">>();

  for (const entry of entries) {
    const anchor = anchorById.get(entry.chemicalAnchorId);
    if (!anchor) continue;

    const group = groups.get(anchor.id) ?? { anchor, entries: [], totalLiters: 0 };
    group.entries.push(entry);
    group.totalLiters += entry.estimatedLiters ?? 0;
    groups.set(anchor.id, group);
  }

  return Array.from(groups.values())
    .map((group) => ({
      ...group,
      cartridges:
        group.anchor.mlQuantity > 0
          ? toOrderQuantity(
              Math.ceil((group.totalLiters * 1000) / group.anchor.mlQuantity),
              group.anchor.minOrderQuantity,
            )
          : 0,
    }))
    .sort((a, b) => a.anchor.code.localeCompare(b.anchor.code));
};

// The hole's diameter × depth with one unit: 14×120mm, or in imperial fractions as the form shows them:
// 13/32×4 59/64"
export const formatHoleSize = (diameter: number, depth: number, isImperial: boolean): string =>
  isImperial
    ? `${millimetersToInchFraction(diameter)}×${millimetersToInchFraction(depth)}"`
    : `${roundMillimeters(diameter)}×${roundMillimeters(depth)}mm`;

// The entry's title - entries have no name: what it measures, as the PDF titles it (report.go's
// entryTitle): M12 - Ø14×120mm - 3 holes. Not its anchor, which its group is titled with.
export const formatEntryTitle = (entry: CalculationEntry, isImperial: boolean, holesLabel: string): string =>
  `M${entry.rodDiameter} - Ø${formatHoleSize(entry.holeDiameter, entry.holeDepth, isImperial)} - ${entry.numHoles} ${holesLabel}`;
